/**
 * Dashboard — Home landing page
 *
 * Shows a welcome message, quick action cards, recent jobs,
 * and pending notifications. Adapts based on org type.
 */

import React, { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Badge,
  Spinner,
} from 'reactstrap';
import moment from 'moment';
import { FirebaseContext } from '../../components/Firebase';
import { useOrganizationStore } from '../../store/organization.store';
import { getCampaignName } from '../../utils/campaign';
import { useNotificationsStore } from '../../store/notifications.store';
import { useAuthStore } from '../../store/auth.store';
import { useTracksStore } from '../../store/tracks.store';
import { withAuthorization, withOrganization } from '../../components/Session';
import { collection, query, orderBy, limit, onSnapshot, getCountFromServer } from 'firebase/firestore';
import { getOrgCollectionPath } from '../../constants/paths';
import * as ROUTES from '../../constants/routes';

interface RecentJob {
  id: string;
  campaign: string;
  client: any;
  period: string;
  status: string;
  stationCount: number;
  /** NOT read from Firestore — computed at render time from
   *  `stations[].linkedTrackId`. The job doc has no `linkedCount`
   *  field; the Jobs page derives it the same way (see Jobs.tsx). */
  linkedCount?: number;
  stations?: any[];
  uploadedAt: number;
  isDeliveredJob?: boolean;
  sourceOrgName?: string;
  /** Denormalized aggregate counters written by shareJobWithBroadcasters
   *  (numerator side is written by the stampDeliveryDownload Cloud
   *  Function). Undefined on legacy jobs — treat as null and hide the
   *  pill until the campaign gets a fresh delivery. */
  deliveryCount?: number;
  downloadedDeliveryCount?: number;
}

/** Live-read spots have no audio to link, so exclude them from the
 *  "linked" denominator — matches the Jobs page's readiness logic at
 *  Jobs.tsx:157 (`getLinkableStationCount`). Without this a campaign
 *  full of live reads would forever show 0/N even when fully ready. */
const getLinkableStationCount = (job: RecentJob): number => {
  const stations = Array.isArray(job.stations) ? job.stations : [];
  const liveReads = stations.filter((s: any) => s?.isLiveRead).length;
  return Math.max(0, (job.stationCount || 0) - liveReads);
};

/** Count of stations whose linkedTrackId still resolves to a real
 *  track in the live tracks store. Dangling links (track deleted from
 *  library) count as unlinked so the badge on this row reflects
 *  reality — matches JobDetail's `hasValidLink` semantics. */
const getLinkedCount = (job: RecentJob, tracks: Record<string, any>): number => {
  const stations = Array.isArray(job.stations) ? job.stations : [];
  return stations.filter((s: any) => s?.linkedTrackId && tracks[s.linkedTrackId]).length;
};

/** Human-readable byte size (e.g. 3.4 GB). Mirrors
 *  audioAnalysisService.formatFileSize; kept local so the dashboard
 *  doesn't pull in the whole audio-analysis module for one formatter. */
const formatBytes = (bytes: number): string => {
  if (!bytes || bytes <= 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const Dashboard: React.FC = () => {
  const history = useHistory();
  const firebase = useContext(FirebaseContext);
  const auth = useAuthStore((s: any) => s.auth);
  const { currentOrgId, currentOrgName, currentOrgType } = useOrganizationStore();
  const isSuperAdmin = useOrganizationStore(state => state.isSuperAdmin);
  const currentOrgRole = useOrganizationStore(state => state.currentOrgRole);

  const pendingReviewsCount = useNotificationsStore(state => state.pendingReviewsCount);
  const reviewResultsCount = useNotificationsStore(state => state.reviewResultsCount);
  const pendingDeliveriesCount = useNotificationsStore(state => state.pendingDeliveriesCount);
  const pendingLicensesCount = useNotificationsStore(state => state.pendingLicensesCount);
  const pendingRequestsCount = useNotificationsStore(state => state.pendingRequestsCount);
  const accessRequestsCount = useNotificationsStore(state => state.accessRequestsCount);
  const accessRequestResultsCount = useNotificationsStore(state => state.accessRequestResultsCount);
  const ftpAccessRequestsCount = useNotificationsStore(state => state.ftpAccessRequestsCount);
  const ftpAccessResultsCount = useNotificationsStore(state => state.ftpAccessResultsCount);
  // In-app prefs — used to hide notification cards for types the user
  // has turned off. Mirrors the same filter the bell-badge total count
  // applies (see notifications.store.ts::getTotalCount). Legacy
  // license-related notifications aren't in the pref system and always
  // show when non-zero.
  const inAppPrefs = useNotificationsStore(state => state.inAppPrefs);
  // Live tracks — passed to getLinkedCount so the linked badge on each
  // recent-jobs row reflects real link validity (dangling linkedTrackIds,
  // e.g. from a track deleted in the library, count as unlinked).
  const tracks = useTracksStore(state => state.tracks);

  const [recentJobs, setRecentJobs] = useState<RecentJob[]>([]);
  // Total campaigns for the Campaigns quick-action card. Sourced from a
  // separate count aggregation so it isn't capped by the recent-jobs
  // subscription's limit(5) — using recentJobs.length as the total
  // silently under-reported (max 5) for any org with more campaigns.
  const [jobCount, setJobCount] = useState<number | null>(null);
  const [trackCount, setTrackCount] = useState<number | null>(null);
  // Total bytes physically stored by THIS org. Summed from the same
  // audiometa docs the count listener already downloads, so it costs no
  // extra reads. Only counts files the org actually stores — see the
  // sourceOrgId exclusion in the listener below.
  const [storageBytes, setStorageBytes] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  const isBroadcaster = currentOrgType === 'broadcaster';
  const isClient = currentOrgType === 'client';
  const isStudio = currentOrgType === 'artist' || !currentOrgType;
  const canEdit = isSuperAdmin || ['ADMIN', 'OPERATOR'].includes(currentOrgRole || '');

  // Live-subscribe to jobs and tracks so counts update without a refresh
  useEffect(() => {
    if (!currentOrgId || !firebase) {
      setLoading(false);
      return;
    }

    const jobsRef = collection(firebase.db, getOrgCollectionPath(currentOrgId, 'jobs'));
    const jobsQuery = query(jobsRef, orderBy('uploadedAt', 'desc'), limit(5));
    const tracksRef = collection(firebase.db, getOrgCollectionPath(currentOrgId, 'audiometa'));

    const unsubJobs = onSnapshot(jobsQuery, (snap: any) => {
      setRecentJobs(snap.docs.map((d: any) => ({ id: d.id, ...d.data() } as RecentJob)));
      setLoading(false);
    }, (err: any) => {
      console.error('[Dashboard] jobs listener error:', err);
      setLoading(false);
    });

    // Count aggregation for the Campaigns quick-action stat — doesn't
    // download docs, so it's cheap even on orgs with hundreds of jobs.
    // Not live; refreshes when currentOrgId changes.
    getCountFromServer(jobsRef)
      .then((agg) => setJobCount(agg.data().count))
      .catch((err) => console.error('[Dashboard] jobs count failed:', err));

    const unsubTracks = onSnapshot(tracksRef, (snap: any) => {
      setTrackCount(snap.size);
      // Sum physical storage. Licensed/delivered copies (isLicensedTrack
      // / isDeliveryTrack) carry a sourceOrgId and point at the SOURCE
      // org's bucket — their bytes live there, not here — so exclude them
      // to avoid counting (and platform-wide double-counting) storage this
      // org doesn't own. All versions/QC takes are separate entries within
      // mp3Files/wavFiles, so every entry is summed. `size` is only present
      // on uploads made after this feature shipped; legacy entries add 0.
      let bytes = 0;
      snap.docs.forEach((d: any) => {
        const t = d.data() || {};
        if (t.sourceOrgId) return;
        const files = [...(t.mp3Files || []), ...(t.wavFiles || [])];
        for (const f of files) bytes += Number(f?.size) || 0;
      });
      setStorageBytes(bytes);
    }, (err: any) => {
      console.error('[Dashboard] tracks listener error:', err);
    });

    return () => {
      unsubJobs();
      unsubTracks();
    };
  }, [currentOrgId, firebase]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const getUserName = () => {
    const name = auth?.displayName || auth?.email?.split('@')[0] || 'there';
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  const getClientLabel = (client: any): string => {
    if (!client) return '';
    if (typeof client === 'object') return client.label || '';
    return client;
  };

  const getStatusBadge = (job: RecentJob) => {
    if (job.isDeliveredJob) {
      return <Badge style={{ backgroundColor: 'color-mix(in srgb, var(--button-success) 20%, transparent)', color: 'var(--button-success)', fontSize: '0.65rem' }}>Received</Badge>;
    }
    switch (job.status) {
      case 'delivered': return <Badge style={{ backgroundColor: 'color-mix(in srgb, var(--color-accent) 20%, transparent)', color: 'var(--color-accent)', fontSize: '0.65rem' }}>Delivered</Badge>;
      // Legacy 'active' falls through to Draft (passive migration).
      default: return <Badge style={{ backgroundColor: 'rgba(255, 255, 255, 0.08)', color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem' }}>Draft</Badge>;
    }
  };

  // Notification summary items. Each pref-gated type is masked out
  // when the user has disabled it in Account → Notifications, matching
  // the bell-badge filter in notifications.store.ts::getTotalCount.
  // Legacy license-related types (pendingLicenses / pendingRequests)
  // aren't in the pref system and always show when non-zero.
  const notifications: { label: string; count: number; action: () => void }[] = [];
  if (inAppPrefs.reviewNotifications && pendingReviewsCount > 0) notifications.push({ label: 'Spots pending your review', count: pendingReviewsCount, action: () => useNotificationsStore.getState().setShowPendingReviewsModal(true) });
  if (inAppPrefs.reviewNotifications && reviewResultsCount > 0) notifications.push({ label: 'Review results to acknowledge', count: reviewResultsCount, action: () => useNotificationsStore.getState().setShowReviewResultsModal(true) });
  if (inAppPrefs.deliveryNotifications && pendingDeliveriesCount > 0) notifications.push({ label: 'Deliveries to acknowledge', count: pendingDeliveriesCount, action: () => useNotificationsStore.getState().setShowDeliveryNotificationsModal(true) });
  if (pendingLicensesCount > 0) notifications.push({ label: 'Licenses to claim', count: pendingLicensesCount, action: () => useNotificationsStore.getState().setShowPendingLicensesModal(true) });
  if (pendingRequestsCount > 0) notifications.push({ label: 'License requests pending', count: pendingRequestsCount, action: () => useNotificationsStore.getState().setShowPendingRequestsModal(true) });
  if (inAppPrefs.accessRequests && accessRequestsCount > 0) notifications.push({ label: 'Access requests to review', count: accessRequestsCount, action: () => useNotificationsStore.getState().setShowAccessRequestsModal(true) });
  if (inAppPrefs.accessRequestResults && accessRequestResultsCount > 0) notifications.push({ label: 'Access request results', count: accessRequestResultsCount, action: () => useNotificationsStore.getState().setShowAccessRequestResultsModal(true) });
  if (inAppPrefs.ftpAccessRequests && ftpAccessRequestsCount > 0) notifications.push({ label: 'FTP access requests', count: ftpAccessRequestsCount, action: () => useNotificationsStore.getState().setShowFtpAccessRequestsModal(true) });
  if (inAppPrefs.ftpAccessResults && ftpAccessResultsCount > 0) notifications.push({ label: 'FTP access results', count: ftpAccessResultsCount, action: () => useNotificationsStore.getState().setShowFtpAccessResultsModal(true) });

  // Quick action cards based on org type
  // Card themes using CSS custom properties from _tokens.scss
  // Each uses the button colour vars with low-opacity gradients
  const cardThemes = {
    accent:  { gradient: 'linear-gradient(135deg, color-mix(in srgb, var(--color-accent) 12%, transparent) 0%, color-mix(in srgb, var(--color-accent) 4%, transparent) 100%)', color: 'var(--color-accent)' },
    success: { gradient: 'linear-gradient(135deg, color-mix(in srgb, var(--button-success) 12%, transparent) 0%, color-mix(in srgb, var(--button-success) 4%, transparent) 100%)', color: 'var(--button-success)' },
    info:    { gradient: 'linear-gradient(135deg, color-mix(in srgb, var(--button-info) 12%, transparent) 0%, color-mix(in srgb, var(--button-info) 4%, transparent) 100%)', color: 'var(--button-info)' },
    warning: { gradient: 'linear-gradient(135deg, color-mix(in srgb, var(--button-warning) 10%, transparent) 0%, color-mix(in srgb, var(--button-warning) 3%, transparent) 100%)', color: 'var(--button-warning)' },
    muted:   { gradient: 'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)', color: 'rgba(255,255,255,0.5)' },
  };

  interface QuickAction { icon: string; label: string; stat: string; description: string; onClick: () => void; theme: typeof cardThemes.accent }
  const quickActions: QuickAction[] = [];

  // Storage-usage suffix for the Library card. Only shown once we have a
  // non-zero figure — an org whose library is entirely referenced
  // (licensed/delivered) tracks stores nothing itself, so we hide the
  // line rather than show a misleading "0 Bytes".
  const libraryDescription = (base: string) =>
    storageBytes > 0 ? `${base} · ${formatBytes(storageBytes)}` : base;

  if (isStudio) {
    if (canEdit) {
      quickActions.push({ icon: 'tim-icons icon-cloud-upload-94', label: 'Upload Spots', stat: '', description: 'Upload new audio spots to the vault', onClick: () => history.push(ROUTES.ADD_AUDIO), theme: cardThemes.accent });
      quickActions.push({ icon: 'tim-icons icon-bullet-list-67', label: 'Create Campaign', stat: '', description: 'Upload a chase list to start a delivery', onClick: () => history.push(ROUTES.JOBS), theme: cardThemes.success });
    }
    quickActions.push({ icon: 'tim-icons icon-single-copy-04', label: 'Library', stat: trackCount !== null ? String(trackCount) : '--', description: libraryDescription('spots in your vault'), onClick: () => history.push(ROUTES.FIND_AUDIO), theme: cardThemes.info });
    quickActions.push({ icon: 'tim-icons icon-send', label: 'Campaigns', stat: jobCount !== null ? String(jobCount) : '--', description: 'active campaigns', onClick: () => history.push(ROUTES.JOBS), theme: cardThemes.warning });
  }

  if (isBroadcaster) {
    quickActions.push({ icon: 'tim-icons icon-single-copy-04', label: 'Library', stat: trackCount !== null ? String(trackCount) : '--', description: libraryDescription('spots received'), onClick: () => history.push(ROUTES.FIND_AUDIO), theme: cardThemes.info });
    quickActions.push({ icon: 'tim-icons icon-send', label: 'Campaigns', stat: jobCount !== null ? String(jobCount) : '--', description: 'received & created', onClick: () => history.push(ROUTES.JOBS), theme: cardThemes.warning });
    if (canEdit) {
      quickActions.push({ icon: 'tim-icons icon-cloud-upload-94', label: 'Upload Spots', stat: '', description: 'Upload your own spots', onClick: () => history.push(ROUTES.ADD_AUDIO), theme: cardThemes.accent });
    }
  }

  if (isClient) {
    quickActions.push({ icon: 'tim-icons icon-single-copy-04', label: 'Library', stat: trackCount !== null ? String(trackCount) : '--', description: libraryDescription('shared spots'), onClick: () => history.push(ROUTES.FIND_AUDIO), theme: cardThemes.info });
    quickActions.push({ icon: 'tim-icons icon-send', label: 'Campaigns', stat: '', description: 'View campaigns', onClick: () => history.push(ROUTES.JOBS), theme: cardThemes.warning });
  }

  if (isSuperAdmin || currentOrgRole === 'ADMIN') {
    quickActions.push({ icon: 'tim-icons icon-chart-bar-32', label: 'Reporting & Analytics', stat: '', description: 'Campaign reports & library analytics', onClick: () => history.push(ROUTES.ADMINANALYTICS), theme: cardThemes.info });
    quickActions.push({ icon: 'tim-icons icon-settings-gear-63', label: 'Settings', stat: '', description: 'Manage your organization', onClick: () => history.push(ROUTES.ADMIN), theme: cardThemes.muted });
  }

  return (
    <div className="wrapper pt-3 pb-3">
      <Container fluid style={{ maxWidth: '1200px' }}>
        {/* Welcome hero */}
        <div style={{
          marginBottom: '1.5rem', paddingTop: '1.5rem', paddingBottom: '1.5rem',
          paddingLeft: '0.5rem', paddingRight: '0.5rem',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ color: '#fff', fontWeight: 300, marginBottom: '0.25rem', fontSize: '1.6rem' }}>
                {getGreeting()}, <strong style={{ fontWeight: 600 }}>{getUserName()}</strong>
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {currentOrgName && (
                  <span style={{
                    padding: '3px 10px', borderRadius: 'var(--border-radius-sm)', fontSize: '0.8rem',
                    backgroundColor: 'color-mix(in srgb, var(--color-accent) 12%, transparent)',
                    color: 'var(--color-accent)',
                    border: '1px solid color-mix(in srgb, var(--color-accent) 20%, transparent)',
                  }}>
                    {currentOrgName}
                  </span>
                )}
                <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.8rem' }}>
                  {useOrganizationStore.getState().getCategoryLabel()}
                </span>
              </div>
            </div>
            {/* Notification summary — compact */}
            {notifications.length > 0 && (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {notifications.map((n, i) => (
                  <div
                    key={i}
                    onClick={n.action}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '6px 12px', borderRadius: 'var(--border-radius-md)', cursor: 'pointer',
                      backgroundColor: 'color-mix(in srgb, var(--button-warning) 8%, transparent)',
                      border: '1px solid color-mix(in srgb, var(--button-warning) 15%, transparent)',
                      transition: 'background-color var(--transition-fast)',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--button-warning) 15%, transparent)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--button-warning) 8%, transparent)'; }}
                  >
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: '20px', height: '20px', borderRadius: '50%',
                      backgroundColor: 'var(--color-accent)', color: '#fff',
                      fontSize: '0.65rem', fontWeight: 700,
                    }}>
                      {n.count}
                    </span>
                    <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{n.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Quick actions */}
        <Row className="mb-4">
          {quickActions.map((action, i) => (
            <Col md={quickActions.length <= 4 ? 3 : 4} sm={6} key={i} className="mb-3">
              <div
                onClick={action.onClick}
                style={{
                  cursor: 'pointer',
                  borderRadius: 'var(--border-radius-lg)',
                  padding: '1.25rem',
                  background: action.theme.gradient,
                  border: '1px solid rgba(255,255,255,0.06)',
                  transition: `transform var(--transition-normal), border-color var(--transition-normal), box-shadow var(--transition-normal)`,
                  position: 'relative',
                  overflow: 'hidden',
                  minHeight: '120px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)';
                  e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {/* Watermark icon */}
                <i className={action.icon} style={{
                  position: 'absolute', right: '-8px', bottom: '-8px',
                  fontSize: '4.5rem', color: action.theme.color, opacity: 0.06,
                  transform: 'rotate(-10deg)',
                }}></i>

                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-sm)' }}>
                    <i className={action.icon} style={{ fontSize: '0.95rem', color: action.theme.color }}></i>
                    <span style={{ color: '#fff', fontWeight: 600, fontSize: '0.85rem' }}>{action.label}</span>
                  </div>
                  {action.stat && (
                    <div style={{ fontSize: '1.75rem', fontWeight: 700, color: action.theme.color, lineHeight: 1, marginBottom: 'var(--spacing-xs)' }}>
                      {action.stat}
                    </div>
                  )}
                </div>
                <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>{action.description}</div>
              </div>
            </Col>
          ))}
        </Row>

        {/* Recent jobs */}
        {loading ? (
          <div className="text-center py-4">
            <Spinner style={{ color: 'var(--color-accent)' }} />
          </div>
        ) : recentJobs.length > 0 && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h5 style={{ margin: 0, color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Recent Campaigns</h5>
              <button
                onClick={() => history.push(ROUTES.JOBS)}
                style={{
                  background: 'none', border: 'none', color: 'var(--color-accent)',
                  cursor: 'pointer', fontSize: '0.8rem',
                }}
              >
                View all &rarr;
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {recentJobs.map(job => {
                // Left-border accent: green for received (broadcaster
                // view of a delivered job), purple for delivered
                // (source view), grey for everything else (draft, or
                // legacy 'active' which now coalesces to draft).
                const statusColor = job.isDeliveredJob ? 'var(--button-success)'
                  : job.status === 'delivered' ? 'var(--color-accent)' : 'rgba(255,255,255,0.15)';
                return (
                  <div
                    key={job.id}
                    onClick={() => history.push(`/jobs/${job.id}`)}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '0.75rem 1rem', borderRadius: 'var(--border-radius-md)', cursor: 'pointer',
                      backgroundColor: 'rgba(255,255,255,0.02)',
                      borderLeft: `3px solid ${statusColor}`,
                      transition: `background-color var(--transition-fast)`,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.02)'; }}
                  >
                    <div>
                      <div style={{ color: '#fff', fontWeight: 500, fontSize: '0.85rem' }}>
                        {getCampaignName(job.campaign)}
                      </div>
                      <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.75rem' }}>
                        {getClientLabel(job.client)}{job.period ? ` \u00B7 ${job.period}` : ''}
                        {job.isDeliveredJob && job.sourceOrgName ? ` \u00B7 from ${job.sourceOrgName}` : ''}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      {/* Linked-tracks badge — matches the downloaded
                          badge's visual language for consistency. Green
                          when every linkable spot has an audio track,
                          amber for partial, muted when zero. */}
                      {(() => {
                        const linked = getLinkedCount(job, tracks);
                        const linkable = getLinkableStationCount(job);
                        const isComplete = linkable > 0 && linked === linkable;
                        const isPartial = linked > 0 && !isComplete;
                        const bg = isComplete
                          ? 'color-mix(in srgb, var(--button-success) 20%, transparent)'
                          : isPartial
                            ? 'rgba(255, 180, 50, 0.2)'
                            : 'rgba(255, 255, 255, 0.08)';
                        const fg = isComplete
                          ? 'var(--button-success)'
                          : isPartial
                            ? '#ffb432'
                            : 'rgba(255,255,255,0.5)';
                        return (
                          <Badge
                            title={`${linked} of ${linkable} spot${linkable === 1 ? '' : 's'} linked to audio`}
                            style={{ backgroundColor: bg, color: fg, fontSize: '0.65rem' }}
                          >
                            {linked}/{linkable} linked
                          </Badge>
                        );
                      })()}
                      {/* Download-progress badge — always shows the
                          X/Y fraction so studios can see partial
                          progress at a glance (e.g. some broadcasters
                          have downloaded, others haven't). Green when
                          everyone downloaded, amber for partial, muted
                          when zero. Hidden entirely on legacy /
                          never-delivered jobs where deliveryCount is
                          undefined. */}
                      {typeof job.deliveryCount === 'number' && job.deliveryCount > 0 && (() => {
                        const downloaded = Number(job.downloadedDeliveryCount) || 0;
                        const total = job.deliveryCount;
                        const isComplete = downloaded === total;
                        const isPartial = downloaded > 0 && !isComplete;
                        const bg = isComplete
                          ? 'color-mix(in srgb, var(--button-success) 20%, transparent)'
                          : isPartial
                            ? 'rgba(255, 180, 50, 0.2)'
                            : 'rgba(255, 255, 255, 0.08)';
                        const fg = isComplete
                          ? 'var(--button-success)'
                          : isPartial
                            ? '#ffb432'
                            : 'rgba(255,255,255,0.5)';
                        return (
                          <Badge
                            title={`${downloaded} of ${total} recipient${total === 1 ? '' : 's'} have downloaded`}
                            style={{ backgroundColor: bg, color: fg, fontSize: '0.65rem' }}
                          >
                            {downloaded}/{total} downloaded
                          </Badge>
                        );
                      })()}
                      {getStatusBadge(job)}
                      <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.7rem', minWidth: '60px', textAlign: 'right' }}>
                        {job.uploadedAt ? moment(job.uploadedAt).fromNow() : ''}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Container>
      <div style={{ position: 'fixed', bottom: '8px', right: '12px', color: 'rgba(255,255,255,0.15)', fontSize: '0.65rem', pointerEvents: 'none' }}>
        v1.29
      </div>
    </div>
  );
};

const condition = (authUser: any) => !!authUser;
export default withOrganization(withAuthorization(condition)(Dashboard));
